import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Search,
  Add,
  Event,
  People,
  Assessment,
  Info,
  Group,
  WhatsApp,
  Mail,
  Message,
  Download,
  Clear
} from '@mui/icons-material';
import MeetingForm from './MeetingForm';
import GroupOverview from './Overview';
import GroupMembers from './Members';
import GroupPerformance from './Performance';
import { groupsApi, meetingsApi, branchesApi } from './api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`group-tabpanel-${index}`}
      aria-labelledby={`group-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Moved OUTSIDE Groups and wrapped in React.memo.
// Previously this was declared inside Groups(), which meant every keystroke
// in the search box (a state update) re-ran Groups() and created a brand new
// GroupInfoCard function/component type. React then unmounted and remounted
// every card (and their Tabs/Overview/Members/Performance children) on every
// keystroke, which is what caused the lag/pause while typing.
const GroupInfoCard = React.memo(function GroupInfoCard({
  group,
  isMobile,
  getResponsiveCardHeight,
  getTabContentHeight,
  formatDate,
  showNotification,
  fetchGroups,
  groups,
  setGroups,
  setSelectedGroup
}) {
  const [localActiveTab, setLocalActiveTab] = useState(0);

  const handleLocalTabChange = (event, newValue) => {
    setLocalActiveTab(newValue);
  };

  const cardStyles = {
    mb: 3,
    boxShadow: 3,
    height: getResponsiveCardHeight(),
    display: 'flex',
    flexDirection: 'column',
    minHeight: isMobile ? '400px' : '500px' // Minimum height for mobile
  };

  const contentAreaStyles = {
    flex: '0 1 auto',
    overflow: 'hidden', // Changed from auto to hidden for better control
    display: 'flex',
    flexDirection: 'column',
    height: 'auto'
  };

  const tabContentStyles = {
    height: getTabContentHeight(),
    overflow: 'auto',
    paddingBottom: '20px', // Fixed: Added padding to show last member fully
    paddingRight: '8px', // Added right padding for better scrollbar spacing
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#bdbdbd',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: '#999',
      }
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '4px',
    },
  };

  return (
    <Card sx={cardStyles}>
      <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Group Header with gradient background */}
        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          background: "linear-gradient(90deg, rgba(25,118,210,1) 0%, rgba(236,26,35,1) 51%, rgba(167,206,53,1) 100%)",
          color: 'white',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          px: isMobile ? 2 : 3,
          py: 0.7,
          mb: 0,
          flexShrink: 0
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                component="h2"
                sx={{ color: 'white', mr: 2, fontWeight: 'bold' }}
              >
                {group.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                ID: {group.id}
              </Typography>
            </Box>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: 2,
              p: 1,
              display: 'flex'
            }}>
              <Tooltip title="Send WhatsApp Message">
                <IconButton sx={{ color: 'white', mr: 1 }} size={isMobile ? "small" : "medium"}>
                  <WhatsApp />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Email">
                <IconButton sx={{ color: 'white', mr: 1 }} size={isMobile ? "small" : "medium"}>
                  <Mail />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Message">
                <IconButton sx={{ color: 'white' }} size={isMobile ? "small" : "medium"}>
                  <Message />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Tabs below the gradient header */}
        <Box sx={{ bgcolor: 'background.default', px: isMobile ? 1 : 2, flexShrink: 0 }}>
          <Tabs
            value={localActiveTab}
            onChange={handleLocalTabChange}
            indicatorColor="secondary"
            textColor="secondary"
            variant={isMobile ? "fullWidth" : "standard"}
            scrollButtons="auto"
          >
            <Tab
              icon={<Info />}
              label={isMobile ? "Info" : "Overview"}
              iconPosition={isMobile ? "top" : "start"}
            />
            <Tab
              icon={<Group />}
              label="Members"
              iconPosition={isMobile ? "top" : "start"}
            />
            <Tab
              icon={<Assessment />}
              label={isMobile ? "Stats" : "Performance"}
              iconPosition={isMobile ? "top" : "start"}
            />
          </Tabs>
        </Box>

        {/* Tab Panels - Scrollable Content Area */}
        <Box sx={contentAreaStyles}>
          <TabPanel value={localActiveTab} index={0}>
            <Box sx={tabContentStyles}>
              <GroupOverview
                group={group}
                formatDate={formatDate}
                setLocalActiveTab={setLocalActiveTab}
              />
            </Box>
          </TabPanel>

          <TabPanel value={localActiveTab} index={1}>
            <Box sx={tabContentStyles}>
              <GroupMembers
                group={group}
                showNotification={showNotification}
                fetchGroups={fetchGroups}
                setGroups={setGroups}
                groups={groups}
                setSelectedGroup={setSelectedGroup}
                // New props for styling
                alternatingBackgrounds={true}
                memberItemStyles={(index) => ({
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                  borderRadius: 2,
                  mb: 2,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  boxShadow: index % 2 === 0 ? '0 1px 3px rgba(0,0,0,0.08)' : '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#e9ecef',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)',
                  }
                })}
              />
            </Box>
          </TabPanel>

          <TabPanel value={localActiveTab} index={2}>
            <Box sx={tabContentStyles}>
              <GroupPerformance group={group} />
            </Box>
          </TabPanel>
        </Box>
      </CardContent>
    </Card>
  );
});

const Groups = ({ 
  containerHeight = 'auto', // Changed default to auto
  cardHeight = 'responsive', // New responsive option
  fixedCardHeight = '600px',
  contentMaxHeight = 'none',
  headerHeight = '64px'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // State for groups and loading
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // State for dialogs
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate state for input
  const [newGroupDialog, setNewGroupDialog] = useState(false);
  const [meetingFormDialog, setMeetingFormDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [branches, setBranches] = useState([]);

  // State for form data - Updated to include meeting preferences
  const [newGroupData, setNewGroupData] = useState({
    name: '',
    location: '',
    description: '',
    branch_id: '',
    preferred_meeting_day: '',
    preferred_week: ''
  });

  // State for notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const purpleColor = '#9C27B0';

  // Meeting day options
  const meetingDays = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Meeting week options
  const meetingWeeks = [
    { value: 'week1', label: 'Week 1' },
    { value: 'week2', label: 'Week 2' },
    { value: 'week3', label: 'Week 3' },
    { value: 'week4', label: 'Week 4' }
  ];

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setCurrentPage(1); // Reset to first page on search
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchInput, searchQuery]);

  // Fetch branches on mount
  useEffect(() => {
    fetchBranches();
  }, []);

  // Calculate responsive heights
  const getResponsiveCardHeight = useCallback(() => {
    if (cardHeight === 'responsive') {
      if (isMobile) return '70vh';
      if (isTablet) return '75vh';
      return '70vh';
    }
    if (cardHeight === 'fixed') return fixedCardHeight;
    if (cardHeight === 'auto') return 'auto';
    return cardHeight;
  }, [cardHeight, isMobile, isTablet, fixedCardHeight]);

  const getContainerHeight = () => {
    if (containerHeight === 'auto') {
      return isMobile ? '100vh' : '100vh';
    }
    return containerHeight;
  };

  const getContentAreaHeight = () => {
    const height = getContainerHeight();
    if (height === '100vh') {
      return `calc(100vh - ${headerHeight} - 24px)`; // 24px for padding
    }
    return height === 'auto' ? 'auto' : `calc(${height} - ${headerHeight} - 24px)`;
  };

  const getTabContentHeight = useCallback(() => {
    const resolvedCardHeight = getResponsiveCardHeight();
    if (resolvedCardHeight.includes('vh')) {
      // Calculate available height for tab content
      const availableHeight = `calc(${resolvedCardHeight} - 140px)`; // Account for header, tabs, padding
      return availableHeight;
    }
    return 'auto';
  }, [getResponsiveCardHeight]);

  const fetchBranches = async () => {
    try {
      const data = await branchesApi.getBranches();
      setBranches(data);
    } catch (err) {
      console.error('Error fetching branches:', err);
      showNotification('Failed to load branches', 'error');
    }
  };

  // Updated fetch groups function with pagination
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching groups:', { searchQuery, currentPage, pageSize });

      const data = await groupsApi.getGroups(searchQuery, currentPage, pageSize);
      console.log("API Response:", data);

      // Handle both paginated and simple responses
      if (data && typeof data === 'object') {
        if (data.results && Array.isArray(data.results)) {
          // Paginated response
          setGroups(data.results);
          setTotalCount(data.count || 0);
          setTotalPages(data.totalPages || Math.ceil((data.count || 0) / pageSize));
        } else if (Array.isArray(data)) {
          // Simple array response
          setGroups(data);
          setTotalCount(data.length);
          setTotalPages(1);
        } else {
          // Fallback
          setGroups([]);
          setTotalCount(0);
          setTotalPages(1);
        }
      } else {
        setGroups([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups. Please try again later.');
      setGroups([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, pageSize]);

  // Fetch groups when search query or page changes
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Function to create a new group
  const handleCreateGroup = async () => {
    try {
      setLoading(true);
      await groupsApi.createGroup(newGroupData);
      setNewGroupDialog(false);
      setNewGroupData({ 
        name: '', 
        location: '', 
        description: '', 
        branch_id: '',
        preferred_meeting_day: '',
        preferred_week: ''
      });
      setCurrentPage(1); // Reset to first page
      await fetchGroups();
      showNotification('Group created successfully!', 'success');
    } catch (err) {
      console.error('Error creating group:', err);
      showNotification('Failed to create group', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Function to show notifications
  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Function to format date strings
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not scheduled';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  }, []);

  return (
    <Box sx={{ 
      height: getContainerHeight(), 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      minHeight: '100vh' // Ensure minimum height
    }}>
      {/* Header Section */}
      <AppBar position="static" color="default" elevation={1} sx={{ flexShrink: 0 }}>
        <Toolbar sx={{ 
          gap: isMobile ? 1 : 2, 
          minHeight: headerHeight,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          py: isMobile ? 1 : 0
        }}>
          <TextField
            placeholder="Search groups..."
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ 
              flexGrow: 1, 
              maxWidth: isMobile ? '100%' : 400,
              minWidth: isMobile ? '200px' : '300px'
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={pageSize}
              label="Per Page"
              onChange={handlePageSizeChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={!isMobile && <Add />}
            onClick={() => setNewGroupDialog(true)}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? "New" : "Create New Group"}
          </Button>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={!isMobile && <Event />}
            onClick={() => setMeetingFormDialog(true)}
            sx={{ bgcolor: purpleColor }}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? "Meeting" : "RECORD MEETING"}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Scrollable Content */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: isMobile ? 2 : 3,
        pb: isMobile ? 8 : 6, // Extra bottom padding for mobile and desktop
        height: getContentAreaHeight(),
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#666', // Darker for dark theme
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#555',
          }
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(255, 255, 255, 0.05)', // Subtle for dark theme
          borderRadius: '4px',
        },
      }}>
        {/* Pagination (top) */}
        {!loading && !error && totalPages > 1 && (
          <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            justifyContent: isMobile ? 'center' : 'flex-end', 
            alignItems: 'center'
          }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        )}

        {loading && groups.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
            <Button size="small" onClick={fetchGroups} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        ) : groups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {searchQuery ? `No groups found for "${searchQuery}"` : 'No groups found'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {searchQuery ? 'Try adjusting your search terms.' : 'Create your first group by clicking the "Create New Group" button.'}
            </Typography>
            {searchQuery ? (
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                sx={{ mr: 2 }}
              >
                Clear Search
              </Button>
            ) : null}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setNewGroupDialog(true)}
            >
              Create New Group
            </Button>
          </Paper>
        ) : (
          <>
            {groups.map((group) => (
              <GroupInfoCard
                key={group.id}
                group={group}
                isMobile={isMobile}
                getResponsiveCardHeight={getResponsiveCardHeight}
                getTabContentHeight={getTabContentHeight}
                formatDate={formatDate}
                showNotification={showNotification}
                fetchGroups={fetchGroups}
                groups={groups}
                setGroups={setGroups}
                setSelectedGroup={setSelectedGroup}
              />
            ))}
            
            {/* Bottom Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? "small" : "large"}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Updated Create Group Dialog with responsive design */}
      <Dialog
        open={newGroupDialog}
        onClose={() => setNewGroupDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile} // Full screen on mobile
      >
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Group Name"
              fullWidth
              value={newGroupData.name}
              onChange={(e) => setNewGroupData({...newGroupData, name: e.target.value})}
              required
            />
            <TextField
              label="Location"
              fullWidth
              value={newGroupData.location}
              onChange={(e) => setNewGroupData({...newGroupData, location: e.target.value})}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Branch</InputLabel>
              <Select
                value={newGroupData.branch_id}
                label="Branch"
                onChange={(e) => setNewGroupData({...newGroupData, branch_id: e.target.value})}
              >
                <MenuItem value="">
                  <em>Select a branch</em>
                </MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Meeting Preferences Section */}
            <Typography variant="subtitle2" sx={{ mt: 2, color: 'text.secondary' }}>
              Meeting Preferences (Optional)
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Meeting Day</InputLabel>
                  <Select
                    value={newGroupData.preferred_meeting_day}
                    label="Preferred Meeting Day"
                    onChange={(e) => setNewGroupData({...newGroupData, preferred_meeting_day: e.target.value})}
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    {meetingDays.map((day) => (
                      <MenuItem key={day.value} value={day.value}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Week</InputLabel>
                  <Select
                    value={newGroupData.preferred_week}
                    label="Preferred Week"
                    onChange={(e) => setNewGroupData({...newGroupData, preferred_week: e.target.value})}
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    {meetingWeeks.map((week) => (
                      <MenuItem key={week.value} value={week.value}>
                        {week.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={newGroupData.description}
              onChange={(e) => setNewGroupData({...newGroupData, description: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewGroupDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={!newGroupData.name || !newGroupData.location || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Meeting Form Dialog */}
      <MeetingForm
        open={meetingFormDialog}
        onClose={() => setMeetingFormDialog(false)}
        groups={groups}
        onSubmit={async (meetingData) => {
          try {
            await meetingsApi.createMeeting(meetingData.groupId, {
              date: meetingData.date,
              meeting_point: meetingData.location,
              officer_name: meetingData.officer,
              notes: meetingData.notes
            });

            showNotification('Meeting recorded successfully!', 'success');
            setMeetingFormDialog(false);
            fetchGroups();
          } catch (err) {
            console.error('Error recording meeting:', err);
            showNotification('Failed to record meeting', 'error');
          }
        }}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Groups;